import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { ROUTES } from '@/constants';
import { LoginForm } from './components/LoginForm';
import { SignupForm } from './components/SignupForm';

/**
 * Container de alto nível para autenticação, seguindo a estrutura do guia:
 * - Tabs para Login e Cadastro
 * - Redireciona para /app se o usuário já estiver autenticado
 * - Responsável apenas por layout e navegação; lógica de formulário fica nos hooks/components
 */
export const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'signup' ? 1 : 0;
  const redirectParam = searchParams.get('redirect');
  const redirectTo = redirectParam && redirectParam.trim() ? redirectParam : null;

  const [tabValue, setTabValue] = useState(initialTab);
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (searchParams.get('tab') === 'signup') {
      setTabValue(1);
    }
  }, [searchParams]);

  // Redirecionar se já estiver logado
  useEffect(() => {
    if (user && !authLoading) {
      navigate(redirectTo || ROUTES.APP);
    }
  }, [user, authLoading, navigate, redirectTo]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#21316a' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container maxWidth={tabValue === 1 ? 'md' : 'sm'}>
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box textAlign="center" mb={4}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img
                src="/images/logo_vcondo.svg"
                alt="V-Condo"
                style={{ maxWidth: '200px', height: 'auto', display: 'block' }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary">
              {tabValue === 0
                ? 'Faça login para acessar o sistema'
                : 'Crie sua conta e comece a usar'}
            </Typography>
          </Box>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ mb: 3 }}
          >
            <Tab icon={<LoginIcon />} label="Login" iconPosition="start" />
            <Tab icon={<PersonAddIcon />} label="Criar Conta" iconPosition="start" />
          </Tabs>

          {tabValue === 0 ? <LoginForm /> : <SignupForm />}

          {tabValue === 0 && (
            <Box mt={2} textAlign="center">
              <Button
                variant="text"
                onClick={() => navigate(ROUTES.ACCOUNT_DELETION_INFO)}
                sx={{ textTransform: 'none' }}
              >
                Quero excluir minha conta
              </Button>
            </Box>
          )}

          <Box mt={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Sistema para Gestão de Demandas
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};


